import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ProjectService } from '../services/projectService';
import { Table } from './ui';
import { getColumnsForTab } from '../config/projectColumnDefinitions';
import ContactDetailsModal from './ContactDetailsModal';
import DiscrepancyDetailsModal from './DiscrepancyDetailsModal';

/**
 * ProjectsTable
 *
 * Standardized projects table using the new UI component library.
 * Replaces TabbedProjectTable.js with a cleaner, more maintainable implementation.
 *
 * Features:
 * - Column definitions per tab (configurable visibility)
 * - Column sorting (job number, due date, float)
 * - Column resizing with auto-size
 * - Real-time updates (via props)
 * - Inline editing (dropdowns, date inputs)
 * - Row click to view subitems or details
 * - Discrepancies display with hover card
 * - Priority toggle
 * - Contact management
 */
const ProjectsTable = ({
  projects = [],
  activeTab,
  tabConfig,
  onUpdateProject,
  onRowClick,
  loading = false,
  updatingProjects = new Set(),
  clients = [],
  employees = [],
  filteredEmployees = [],
  sortBy = 'dueDate',
  sortDirection = 'asc',
  onSort,
  discrepanciesByProject = {},
  autoResizeEnabled: propAutoResizeEnabled = true,
  onDisableAutoResize,
  hiddenColumns = [],
}) => {

  // State for contacts by project
  const [contactsByProjectId, setContactsByProjectId] = useState({});
  const [loadingContacts, setLoadingContacts] = useState({});

  // State for modals
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [selectedDiscrepancyId, setSelectedDiscrepancyId] = useState(null);

  // Auto-resize state - use prop value if provided
  const autoResizeEnabled = propAutoResizeEnabled;
  const [triggerAutoResize, setTriggerAutoResize] = useState(0);

  // Listen for auto-resize event from parent
  useEffect(() => {
    const handleAutoResize = () => {
      // Force re-render by toggling a state value
      // This will trigger autoResizeOnDataChange in Table component
      setTriggerAutoResize(prev => prev + 1);
    };

    window.addEventListener('projects-table-auto-resize', handleAutoResize);
    return () => {
      window.removeEventListener('projects-table-auto-resize', handleAutoResize);
    };
  }, []);

  // Fetch contacts for all visible projects
  useEffect(() => {
    const fetchContactsForProjects = async () => {
      // Get unique client IDs from visible projects
      const clientIds = [...new Set(projects.map(p => p.clientId).filter(Boolean))];

      if (clientIds.length === 0) {
        setContactsByProjectId({});
        return;
      }

      // Set loading state for all projects
      const initialLoadingState = {};
      projects.forEach(project => {
        if (project.clientId) {
          initialLoadingState[project.id] = true;
        }
      });
      setLoadingContacts(initialLoadingState);

      // Fetch contacts for all unique clients in parallel
      const clientContactsMap = {};
      try {
        const contactsFetchPromises = clientIds.map(clientId =>
          ProjectService.getContactsByClient(clientId)
            .then(contacts => ({ clientId, contacts }))
            .catch(err => {
              console.error(`Failed to fetch contacts for client ${clientId}:`, err);
              return { clientId, contacts: [] };
            })
        );

        const results = await Promise.all(contactsFetchPromises);

        // Map client contacts to a lookup
        results.forEach(({ clientId, contacts }) => {
          clientContactsMap[clientId] = contacts;
        });

        // Map contacts by project ID
        const newContactsByProject = {};
        projects.forEach(project => {
          if (project.clientId && clientContactsMap[project.clientId]) {
            newContactsByProject[project.id] = clientContactsMap[project.clientId];
          } else {
            newContactsByProject[project.id] = [];
          }
        });

        setContactsByProjectId(newContactsByProject);
        setLoadingContacts({});

        // Trigger auto-resize after contacts finish loading
        // This ensures the contact column is properly sized for the actual contact names
        setTimeout(() => {
          if (autoResizeEnabled) {
            setTriggerAutoResize(prev => prev + 1);
          }
        }, 100);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setLoadingContacts({});
      }
    };

    if (projects.length > 0) {
      fetchContactsForProjects();
    }
  }, [projects, autoResizeEnabled]);

  // Handle contact info click
  const handleContactInfoClick = useCallback((e, contact, project) => {
    e.stopPropagation();
    e.preventDefault();
    // Add client name to contact data for proper display in modal
    const enrichedContact = {
      ...contact,
      clientName: project?.clientName || 'Unknown Client',
      // Normalize field names for ContactDetailsModal
      clientId: contact.client_id,
      phoneNumber: contact.phone_number,
    };
    setSelectedContact(enrichedContact);
    setShowContactModal(true);
  }, []);

  // Handle discrepancy click - open modal in-place instead of navigating
  const handleDiscrepancyClick = useCallback((e, discrepancyId) => {
    e.stopPropagation();
    setSelectedDiscrepancyId(discrepancyId);
    setShowDiscrepancyModal(true);
  }, []);

  // Handle row click
  const handleRowClick = useCallback((project, type = 'subitems') => {
    if (onRowClick) {
      onRowClick(project, type);
    }
  }, [onRowClick]);

  // Handle manual resize - turn off auto-resize when user manually resizes a column
  const handleManualResize = useCallback(() => {
    if (onDisableAutoResize) {
      onDisableAutoResize();
    }
  }, [onDisableAutoResize]);

  // Get columns for current tab
  const columns = useMemo(() => {
    const allColumns = getColumnsForTab(activeTab, tabConfig, {
      onUpdateProject,
      onRowClick: handleRowClick,
      employees,
      filteredEmployees,
      updatingProjects,
      handleContactInfoClick,
      handleDiscrepancyClick,
      discrepanciesByProject,
      loadingContacts,
      contactsByProjectId,
    });

    // Filter out hidden columns if provided
    if (hiddenColumns.length > 0) {
      return allColumns.filter(col => !hiddenColumns.includes(col.id));
    }

    return allColumns;
  }, [
    activeTab,
    tabConfig,
    onUpdateProject,
    handleRowClick,
    employees,
    filteredEmployees,
    updatingProjects,
    handleContactInfoClick,
    handleDiscrepancyClick,
    discrepanciesByProject,
    loadingContacts,
    contactsByProjectId,
    hiddenColumns,
  ]);

  // Split projects into regular and soft hold
  const regularProjects = useMemo(() => projects.filter(p => !p.isSoftHold), [projects]);
  const softHoldProjects = useMemo(() => projects.filter(p => p.isSoftHold), [projects]);

  return (
    <>
      {/* Regular Projects Table */}
      <Table
        key={`${activeTab}-regular-${triggerAutoResize}`}
        data={regularProjects}
        columns={columns}

        // Sorting (handled by parent - data comes pre-sorted)
        sortable={false} // Disable internal sorting, parent handles it

        // Features
        searchable={false} // Search handled by parent
        resizable={true}
        paginated={false} // No pagination for projects table
        autoResizeOnDataChange={autoResizeEnabled}
        showAutoSizeButton={false} // Hidden, parent has custom button
        stickyHeader={true}
        disableResizeListener={true} // Disable window resize listener to prevent aggressive auto-sizing
        allowNaturalWidth={true} // Use natural column widths with horizontal scrolling

        // State
        loading={loading}
        emptyMessage="No projects found in this tab"

        // Events
        // Note: onRowClick removed - specific columns (job number, project name, client, eye icon) handle their own clicks
        onManualResize={handleManualResize}
        getRowClassName={(project) => project.isHighPriority ? 'cellHighPriority' : ''}

        // Styling
        variant="dense"
        storageKey={`projects-table-${activeTab}`}

        // Pass through styles
        style={{
          border: 'none',
          borderRadius: '0',
          width: '100%',
          overflow: 'visible'
        }}
      />

      {/* Soft Hold Section */}
      {softHoldProjects.length > 0 && (
        <>
          {/* Simple Text + Line Separator */}
          <div style={{
            marginTop: '32px',
            marginBottom: '0',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#eab308',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="4" width="4" height="16"></rect>
                <rect x="14" y="4" width="4" height="16"></rect>
              </svg>
              Paused ({softHoldProjects.length})
            </div>
            <div style={{
              height: '1px',
              background: 'rgba(234, 179, 8, 0.3)',
            }}></div>
          </div>

          {/* Soft Hold Projects Table */}
          <div className="softhold-table-wrapper">
            <Table
              key={`${activeTab}-softhold-${triggerAutoResize}`}
              data={softHoldProjects}
              columns={columns}

              // Sorting (handled by parent - data comes pre-sorted)
              sortable={false}

              // Features
              searchable={false}
              resizable={true}
              paginated={false}
              autoResizeOnDataChange={false}
              showAutoSizeButton={false}
              stickyHeader={false}
              disableResizeListener={true} // Disable window resize listener to prevent aggressive auto-sizing
              allowNaturalWidth={true} // Use natural column widths with horizontal scrolling

              // State
              loading={loading}
              emptyMessage=""

              // Events
              onManualResize={handleManualResize}
              getRowClassName={() => 'cellSoftHold'}

              // Styling
              variant="dense"
              storageKey={`projects-table-${activeTab}`}

              // Pass through styles
              style={{
                border: 'none',
                borderRadius: '0',
                width: '100%',
                overflow: 'visible'
              }}
            />
          </div>
        </>
      )}

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactModal}
          onClose={() => {
            setShowContactModal(false);
            setSelectedContact(null);
          }}
          initialEditMode={false}
          onContactUpdated={() => {
            // Contact was updated - the contact list will be refreshed by the useEffect
            setShowContactModal(false);
            setSelectedContact(null);
          }}
        />
      )}

      {/* Discrepancy Details Modal */}
      {showDiscrepancyModal && selectedDiscrepancyId && (
        <DiscrepancyDetailsModal
          discrepancyId={selectedDiscrepancyId}
          isOpen={showDiscrepancyModal}
          onClose={() => {
            setShowDiscrepancyModal(false);
            setSelectedDiscrepancyId(null);
          }}
        />
      )}
    </>
  );
};

export default ProjectsTable;
